import { createContext, useState, useRef, useCallback, useMemo, useImperativeHandle, useEffect } from 'react';
import { useValidatedContext } from '../hooks/use-validated-context.js';
import { Surface } from '../surface/surface.js';
import { removeByValue } from '../utils/array.js';
import { accept, isPortaledEvent } from '../utils/event.js';
import { createFocusManager } from '../utils/focus.js';
import { getMovementType } from '../utils/interaction.js';
import { isPrintableKey, isModifierKey } from '../utils/key-code.js';
import { getModifiers, isStandardShortcutKeyPressed } from '../utils/modifiers.js';
import { Positioner } from '../utils/positioner/positioner.js';
import { normalizeTypeaheadText, getItemLabel } from '../utils/typeahead.js';
import { VIEWPORT_MARGIN } from './menu-v2.css.js';

/* eslint-disable @typescript-eslint/no-unsafe-enum-comparison */
const MenuInternalContext = createContext(null);
/** Handle individual item affecting selection */
function useMenuItemSelection({ targetId, } = {}) {
    // Roving tabindex -- when you tab out of the window and back in, the previously focused item
    // should retain focus.
    // TODO: this could be done without state in the menu root. See `TabPrimitive` for an example.
    const [hasFocus, setHasFocus] = useState(false);
    // Close the submenu that's registered with the parent menu when this is focused
    const { isClosing, programmaticOpen, registerOpenSubMenu } = useValidatedContext(MenuInternalContext, 'MenuV2.Item', 'MenuV2.Root');
    const { endProgrammaticOpen, programmaticTargetId, registerMenuTarget, releaseProgrammaticPointerItem, shouldIgnoreProgrammaticPointerEvent, } = programmaticOpen;
    const isProgrammaticTarget = Boolean(targetId && targetId === programmaticTargetId);
    const targetRef = useMenuTargetRef(targetId, registerMenuTarget);
    const movementType = getMovementType();
    const itemProps = {
        tabIndex: hasFocus ? 0 : -1,
        // this handles the default selection behavior
        onPointerMove(e) {
            if (isClosing.current)
                return;
            if (shouldIgnoreProgrammaticPointerEvent(e.currentTarget))
                return;
            endProgrammaticOpen();
            e.currentTarget.focus();
        },
        // this is only for the edge case where a mouse “drops in” from the arrow disappearing (cursor doesn’t move)
        onPointerEnter(e) {
            if (isClosing.current)
                return;
            if (shouldIgnoreProgrammaticPointerEvent(e.currentTarget))
                return;
            // Note: if a user triggers a scroll event via keyboard, and a mouse
            // pointer is still hovering over a submenu, this will fire, and cause
            // jank. It’s a bit of an edge case, but still happens, hence the
            // seemingly-useless check (which actually isn’t)
            if (movementType !== 'pointer')
                return;
            endProgrammaticOpen();
            e.currentTarget.focus();
        },
        onPointerLeave(e) {
            releaseProgrammaticPointerItem(e.currentTarget);
        },
        onFocus() {
            setHasFocus(true);
            registerOpenSubMenu(undefined);
        },
        onBlur() {
            setHasFocus(false);
        },
        'data-fpl-programmatic-selected': isProgrammaticTarget || undefined,
        'data-movement-type': movementType,
    };
    return { itemProps, targetRef };
}
/** Apply the active Surface treatment for a visual menu item. */
function useMenuItemSurfaceProps({ targetId, disabled, treatment, }) {
    const { interactionMode, programmaticOpen } = useValidatedContext(MenuInternalContext, 'MenuV2.Item', 'MenuV2.Root');
    const isProgrammaticTarget = Boolean(targetId && targetId === programmaticOpen.programmaticTargetId);
    // Only expose the treatment for the current interaction mode so a stationary pointer and
    // keyboard focus cannot highlight two items at once.
    const { interactionProps, paintProps } = Surface.getSplitProps({
        disabled,
        color: !disabled && isProgrammaticTarget && treatment,
        hover: interactionMode === 'pointer' && treatment,
        focusVisible: interactionMode === 'keyboard' && treatment,
    });
    return { interactionProps, paintProps };
}
/** Register a programmatic menu target while its element is mounted. */
function useMenuTargetRef(targetId, registerMenuTarget) {
    const unregisterRef = useRef(undefined);
    return useCallback((element) => {
        unregisterRef.current?.();
        unregisterRef.current = undefined;
        if (!targetId || !element)
            return;
        unregisterRef.current = registerMenuTarget(targetId, element);
    }, [registerMenuTarget, targetId]);
}
/** Coordinate imperative menu paths, focus, and initial pointer suppression. */
function useProgrammaticMenuOpen(manager) {
    const { open, setOpen, private: managerPrivate } = manager;
    const pendingTargetPath = useRef(undefined);
    const registeredTargets = useRef(new Map());
    const ignoredPointerItem = useRef(undefined);
    const pointerCaptureFrame = useRef(0);
    const [targetPath, setTargetPath] = useState();
    const targetId = targetPath?.at(-1);
    const clearPointerCapture = useCallback(() => {
        window.cancelAnimationFrame(pointerCaptureFrame.current);
        ignoredPointerItem.current = undefined;
        pointerCaptureFrame.current = 0;
    }, []);
    const beginPointerCapture = useCallback(() => {
        clearPointerCapture();
        pointerCaptureFrame.current = window.requestAnimationFrame(() => {
            pointerCaptureFrame.current = 0;
        });
    }, [clearPointerCapture]);
    const endProgrammaticOpen = useCallback(() => {
        pendingTargetPath.current = undefined;
        clearPointerCapture();
        setTargetPath(undefined);
    }, [clearPointerCapture]);
    const shouldIgnoreProgrammaticPointerEvent = useCallback((item) => {
        if (ignoredPointerItem.current === item)
            return true;
        if (pointerCaptureFrame.current === 0)
            return false;
        window.cancelAnimationFrame(pointerCaptureFrame.current);
        pointerCaptureFrame.current = 0;
        ignoredPointerItem.current = item;
        return true;
    }, []);
    const releaseProgrammaticPointerItem = useCallback((item) => {
        if (ignoredPointerItem.current !== item)
            return;
        ignoredPointerItem.current = undefined;
    }, []);
    const registerMenuTarget = useCallback((registeredTargetId, element) => {
        registeredTargets.current.set(registeredTargetId, element);
        const pendingPath = pendingTargetPath.current;
        if (pendingPath?.at(-1) === registeredTargetId) {
            pendingTargetPath.current = undefined;
            beginPointerCapture();
            setTargetPath(pendingPath);
            // Callback refs run before ancestor layout effects. Defer focus until the full popup tree
            // has attached its refs and completed its own initial focus setup.
            queueMicrotask(() => {
                if (!element.isConnected)
                    return;
                if (registeredTargets.current.get(registeredTargetId) !== element)
                    return;
                element.focus();
            });
        }
        return () => {
            if (registeredTargets.current.get(registeredTargetId) !== element)
                return;
            registeredTargets.current.delete(registeredTargetId);
        };
    }, [beginPointerCapture]);
    useImperativeHandle(managerPrivate.rootRef, () => ({
        openSubMenu(target) {
            const nextTargetPath = typeof target === 'string' ? [target] : [...target];
            if (nextTargetPath.length === 0)
                return;
            pendingTargetPath.current = nextTargetPath;
            beginPointerCapture();
            setTargetPath(nextTargetPath);
            setOpen(true);
            const element = registeredTargets.current.get(nextTargetPath.at(-1) ?? '');
            if (!element)
                return;
            pendingTargetPath.current = undefined;
            element.focus();
        },
    }), [beginPointerCapture, setOpen]);
    useEffect(() => {
        if (!open)
            endProgrammaticOpen();
    }, [endProgrammaticOpen, open]);
    useEffect(() => () => clearPointerCapture(), [clearPointerCapture]);
    useEffect(() => {
        if (!open || !targetId || registeredTargets.current.has(targetId))
            return;
        // Keep the request pending so a target rendered by a loading state can still fulfill it.
        clearPointerCapture();
        setTargetPath(undefined);
    }, [clearPointerCapture, open, targetId]);
    return useMemo(() => ({
        programmaticTargetId: targetId,
        programmaticTargetPath: targetPath,
        registerMenuTarget,
        endProgrammaticOpen,
        releaseProgrammaticPointerItem,
        shouldIgnoreProgrammaticPointerEvent,
    }), [
        endProgrammaticOpen,
        registerMenuTarget,
        releaseProgrammaticPointerItem,
        shouldIgnoreProgrammaticPointerEvent,
        targetId,
        targetPath,
    ]);
}
/** This hook only requires different options for the main menu vs. submenu */
function useMenuPositioner({ position }) {
    return useMemo(() => Positioner.create({ viewportMargin: VIEWPORT_MARGIN })
        .position(position)
        .fallback((p) => [p('y').flip(), p('y').shift().clip()])
        .fallback((p) => [p('x').flip(), p('x').shift()]), [position]);
}
/** Ensures only one submenu can be open at once in a given container */
function useSubMenuContainer() {
    const registeredSubMenuClose = useRef([]);
    return useMemo(() => ({
        registerOpenSubMenu: (close) => {
            // Note: this is an array (“queue”), because under times of CPU stress,
            // these will stack up more quickly than they can tear down. By
            // maintaining a queue at all times, multiple submenus can stack opening
            // and closing correctly, without any being orphaned
            let nextClose = registeredSubMenuClose.current.shift();
            while (typeof nextClose === 'function') {
                nextClose();
                nextClose = registeredSubMenuClose.current.shift();
            }
            if (!close)
                return () => { };
            registeredSubMenuClose.current.push(close);
            return () => {
                removeByValue(registeredSubMenuClose.current, close);
            };
        },
    }), []);
}
/** Handle keyboard events */
function useMenuNav({ popupRef }) {
    const focusManager = useMemo(() => createFocusManager(popupRef, { criteria: 'focusableNonDisabled' }), [popupRef]);
    const groupFocusManager = useMemo(() => {
        return createFocusManager(popupRef, { criteria: 'firstFocusableMenuGroup' });
    }, [popupRef]);
    const onKeyDown = useCallback((e) => {
        const mods = getModifiers(e);
        if (e.key === 'ArrowUp' && isStandardShortcutKeyPressed(e)) {
            groupFocusManager.focusPrevious({ wrap: !e.repeat });
            accept(e);
        }
        else if ((e.key === 'ArrowUp' && !mods) || (e.key === 'p' && mods === 8 /* Modifier.CONTROL */)) {
            focusManager.focusPrevious({ wrap: !e.repeat });
            accept(e);
        }
        else if (e.key === 'ArrowDown' && isStandardShortcutKeyPressed(e)) {
            groupFocusManager.focusNext({ wrap: !e.repeat });
            accept(e);
        }
        else if ((e.key === 'ArrowDown' && !mods) || (e.key === 'n' && mods === 8 /* Modifier.CONTROL */)) {
            focusManager.focusNext({ wrap: !e.repeat });
            accept(e);
        }
        else if (e.key === 'Home' && !mods) {
            focusManager.focusFirst();
            accept(e);
        }
        else if (e.key === 'End' && !mods) {
            focusManager.focusLast();
            accept(e);
        }
    }, [focusManager, groupFocusManager]);
    return useMemo(() => ({
        focusManager,
        popupProps: { onKeyDown },
    }), [focusManager, onKeyDown]);
}
/**
 * Returns an `onKeyDownCapture` handler that can be attached to the popup that will focus the
 * descendent menu item that matches the typeahead. This needs to happen in the capture phase to
 * happen before the menu item's event handlers so spacebar can be handled.
 */
function useMenuTypeahead({ popupRef, timeout = 1000, }) {
    const timeoutRef = useRef();
    const queryRef = useRef('');
    const menuItemCacheRef = useRef();
    // Clear any pending typeahead timer on unmount so it can’t fire after teardown
    useEffect(() => () => window.clearTimeout(timeoutRef.current), []);
    const onKeyDownCapture = useCallback((e) => {
        if (isPortaledEvent(e)) ;
        else if (e.key === ' ' && queryRef.current === '') ;
        else if (isPrintableKey(e)) {
            // The user typed a printable key, so we should start or continue the typeahead behavior
            window.clearTimeout(timeoutRef.current);
            timeoutRef.current = window.setTimeout(() => {
                window.clearTimeout(timeoutRef.current);
                timeoutRef.current = undefined;
                queryRef.current = '';
                menuItemCacheRef.current = undefined;
            }, timeout);
            queryRef.current += normalizeTypeaheadText(e.key);
            // Find the element that matches the query.
            if (!menuItemCacheRef.current) {
                const els = popupRef.current?.querySelectorAll('[role^="menuitem"]') ?? [];
                menuItemCacheRef.current = new Map([...els].map((el) => [normalizeTypeaheadText(getItemLabel(el)), el]));
            }
            if (menuItemCacheRef.current) {
                for (const [text, menuItemEl] of menuItemCacheRef.current) {
                    if (text.startsWith(queryRef.current)) {
                        // One was found, focus it and break.
                        menuItemEl.focus();
                        break;
                    }
                }
            }
            // We should accept(e) on every printable keystroke, even if a match wasn't found.
            accept(e);
        }
        else if (!isModifierKey(e.key)) {
            // A non-printable key was typed, so clear the typeahead behavior and let events pass.
            // Modifier keys alone shouldn't reset the typeahead
            window.clearTimeout(timeoutRef.current);
            timeoutRef.current = undefined;
            queryRef.current = '';
            menuItemCacheRef.current = undefined;
        }
    }, [popupRef, timeout]);
    return useMemo(() => ({ onKeyDownCapture }), [onKeyDownCapture]);
}

export { MenuInternalContext, useMenuItemSelection, useMenuItemSurfaceProps, useMenuNav, useMenuPositioner, useMenuTargetRef, useMenuTypeahead, useProgrammaticMenuOpen, useSubMenuContainer };
//# sourceMappingURL=menu-v2.hooks.js.map
